/**
 * ClaimNotificationContext — Global in-app alert when parents claim items
 * from a Communication with needed items.
 *
 * Polls /api/communications/history every 10s while the browser tab is
 * visible, compares per-item `claimed` counts against the previous snapshot,
 * and when any item's claimed count has gone up:
 *   1. plays the coin / cash-register sound
 *   2. shows a Sonner toast with 🎉 and the claim summary
 *
 * If multiple claims happen within one poll cycle, they are combined into a
 * single beep + stacked toast (per owner preference).
 */
import { createContext, useContext, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { playCoinSound } from "@/utils/notificationSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 10_000;

const ClaimNotificationContext = createContext(null);

export const ClaimNotificationProvider = ({ children }) => {
  const { token } = useAuth();
  const snapshotRef = useRef(null);     // { [commId]: { [itemId]: claimedCount } }
  const intervalRef = useRef(null);
  const isFirstPollRef = useRef(true);

  const buildSnapshot = (communications) => {
    const snap = {};
    for (const c of communications || []) {
      if (!c?.id || !Array.isArray(c.neededItems)) continue;
      snap[c.id] = {};
      for (const it of c.neededItems) {
        if (it?.id != null) {
          snap[c.id][it.id] = it.claimed || 0;
        }
      }
    }
    return snap;
  };

  const detectNewClaims = (prev, next, communications) => {
    // Returns a list of { commSubject, itemName, parentName }
    const newClaims = [];
    for (const c of communications || []) {
      if (!c?.id || !Array.isArray(c.neededItems)) continue;
      const prevComm = prev?.[c.id] || {};
      for (const it of c.neededItems) {
        const beforeCount = prevComm[it.id] ?? 0;
        const afterCount = it.claimed || 0;
        if (afterCount > beforeCount) {
          // Try to surface the latest claimer's name from claimedBy
          const latestClaimers = Array.isArray(it.claimedBy) ? it.claimedBy.slice(-1 * (afterCount - beforeCount)) : [];
          if (latestClaimers.length > 0) {
            for (const cb of latestClaimers) {
              newClaims.push({
                commSubject: c.subject || "event",
                itemName: it.name || "an item",
                parentName: (cb && cb.name) || "A parent",
              });
            }
          } else {
            newClaims.push({
              commSubject: c.subject || "event",
              itemName: it.name || "an item",
              parentName: "A parent",
            });
          }
        }
      }
    }
    return newClaims;
  };

  const pollOnce = async () => {
    try {
      // EXPLICIT auth header to avoid first-poll race where axios default
      // isn't set yet (was causing a harmless 403 in console on page load).
      // To revert: replace next line with `const res = await axios.get(`${API}/communications/history?limit=50`);`
      const res = await axios.get(`${API}/communications/history?limit=50`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const communications = res.data?.communications || [];
      const nextSnap = buildSnapshot(communications);

      if (isFirstPollRef.current) {
        // Seed the baseline silently — we do NOT beep for state that already exists.
        snapshotRef.current = nextSnap;
        isFirstPollRef.current = false;
        return;
      }

      const newClaims = detectNewClaims(snapshotRef.current, nextSnap, communications);
      snapshotRef.current = nextSnap;

      if (newClaims.length === 0) return;

      // Single beep, stacked toast (owner preference for rapid-claim throttle)
      try { playCoinSound(); } catch (e) { /* audio may be blocked */ }

      if (newClaims.length === 1) {
        const c = newClaims[0];
        toast.success(`🎉 ${c.parentName} just claimed ${c.itemName}`, {
          description: c.commSubject,
          duration: 8000,
        });
      } else {
        const lines = newClaims.slice(0, 5)
          .map(c => `${c.parentName}: ${c.itemName}`)
          .join("\n");
        const more = newClaims.length > 5 ? `\n+${newClaims.length - 5} more` : "";
        toast.success(`🎉 ${newClaims.length} new claims`, {
          description: `${lines}${more}`,
          duration: 10000,
        });
      }
    } catch (error) {
      // Silent — don't spam the console on transient failures
      // console.debug("[ClaimNotification] poll failed:", error?.message);
    }
  };

  useEffect(() => {
    if (!token) {
      // Clear any running interval if user logs out
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      snapshotRef.current = null;
      isFirstPollRef.current = true;
      return;
    }

    // Reset baseline on login
    snapshotRef.current = null;
    isFirstPollRef.current = true;

    const tick = () => {
      // Only poll while the tab is visible to save CPU / network
      if (document.visibilityState !== "visible") return;
      pollOnce();
    };

    // Kick off one initial poll (seeds the baseline), then schedule
    tick();
    intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [token]);

  return (
    <ClaimNotificationContext.Provider value={{}}>
      {children}
    </ClaimNotificationContext.Provider>
  );
};

export const useClaimNotifications = () => useContext(ClaimNotificationContext);
